import { useState } from 'react';
import { influencer } from '@round/api';
import { VirtualizedTable, tableHelpers } from '@round/creatorbase-ui';
import { CreatorTableEmptyState } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/EmptyState/CreatorTableEmptyState';
import { Column } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { useCreatorExplorePlatformSelection } from 'Modules/Plans/CreatorSearch/hooks/useCreatorExplorePlatformSelection';
import { ROW_HEIGHT_PX, getCreatorColumns } from '../AddCreatorsTable/helpers';
import { RemoveFromSelectionCell } from './components/RemoveFromSelectionCell/RemoveFromSelectionCell';
import { useSelectedCreators } from './useSelectedCreators';
import { Meta } from './helpers';

type Props = {
    selectedCreatorIds: Set<string>;
    getCreatorPoolAccounts: Meta['getCreatorPoolAccounts'];
    getIsCreatorSelected: (creator: influencer.CreatorSearchResult) => boolean;
    onCreatorRemove: (creator: influencer.CreatorSearchResult) => void;
    /** A bulk add posts the selection it captured on click, so a removal mid-add would not reach it. */
    isRemoveDisabled: boolean;
    className?: string;
};

/**
 * Deliberately unsortable: server sorting cannot reach a local list, and most columns render from cells
 * with no value to sort on.
 *
 * Leaving the view has to unmount this, which is what freezes the ids it fetches. Kept mounted and merely
 * hidden, it would hold the list to whatever was selected the first time it rendered.
 */
export const SelectedCreators = ({
    selectedCreatorIds,
    getCreatorPoolAccounts,
    getIsCreatorSelected,
    onCreatorRemove,
    isRemoveDisabled,
    className,
}: Props) => {
    const [creatorIds] = useState(() => [...selectedCreatorIds]);

    const { data, isLoading, hasError, getCreatorImages, getAreCreatorImagesLoading } = useSelectedCreators(creatorIds);

    // Its own rather than the drawer's, so the choice does not outlive the visit.
    const { getRowSelectedPlatform, onRowPlatformSelect } = useCreatorExplorePlatformSelection();

    const tableMeta: Meta = {
        isLoading,
        hasError,
        getCreatorImages,
        getAreCreatorImagesLoading,
        getRowSelectedPlatform,
        onRowPlatformSelect,
        getCreatorPoolAccounts,
    };

    // Against the live selection, not the ids fetched for, so a removal costs no refetch.
    const creators = data.filter(getIsCreatorSelected);

    const columns: Column[] = [
        {
            header: undefined,
            id: 'actions',
            cell: (context) => (
                <RemoveFromSelectionCell {...context} onRemove={onCreatorRemove} isDisabled={isRemoveDisabled} />
            ),
            enableSorting: false,
            size: 70,
        },
        ...getCreatorColumns(),
    ];

    return (
        <VirtualizedTable
            columns={columns}
            data={tableHelpers.getLoadingRows(creators, { pageSize: creatorIds.length, isLoading })}
            meta={tableMeta}
            virtualizerOptions={{
                estimateSize: () => ROW_HEIGHT_PX,
                getItemKey: (index) => `${index}-${creators[index]?._id}` || `skeleton-${index}`,
                overscan: 50,
            }}
            containerClassName={className}
            columnPinning={{ left: ['actions', 'creator'] }}
            noDataLabel={<CreatorTableEmptyState hasError={hasError} />}
        />
    );
};
